import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { createEvent, deleteEvent, getAllEvents, updateEvent } from '../../services/eventsService';
import '../geo/geoScenes.css';

const emptyForm = {
  id: null,
  name: '',
  description: '',
  startAt: '',
  endAt: '',
  xpMultiplier: 1
};

const toLocalInputValue = (isoOrEmpty) => {
  if (!isoOrEmpty) return '';
  const d = new Date(isoOrEmpty);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const statusOf = (ev) => {
  const now = Date.now();
  const start = ev.startAt ? new Date(ev.startAt).getTime() : null;
  const end = ev.endAt ? new Date(ev.endAt).getTime() : null;
  if (start != null && now < start) return { label: 'Programado', tone: 'upcoming' };
  if (end != null && now > end) return { label: 'Finalizado', tone: 'ended' };
  return { label: 'Activo', tone: 'active' };
};

export default function EventsAdmin() {
  const { enqueueSnackbar } = useSnackbar();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await getAllEvents();
      list.sort((a, b) => new Date(b.startAt || 0) - new Date(a.startAt || 0));
      setEvents(list);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar los eventos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (ev) => {
    setForm({
      id: ev.id,
      name: ev.name || '',
      description: ev.description || '',
      startAt: toLocalInputValue(ev.startAt),
      endAt: toLocalInputValue(ev.endAt),
      xpMultiplier: Number(ev.xpMultiplier) || 1
    });
  };

  const handleReset = () => setForm(emptyForm);

  const handleSave = async () => {
    if (!form.name.trim()) {
      enqueueSnackbar('El nombre del evento es obligatorio', { variant: 'warning' });
      return;
    }
    if (!form.startAt || !form.endAt) {
      enqueueSnackbar('Definí fecha de inicio y de fin', { variant: 'warning' });
      return;
    }
    if (new Date(form.endAt).getTime() <= new Date(form.startAt).getTime()) {
      enqueueSnackbar('La fecha de fin debe ser posterior al inicio', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        xpMultiplier: Math.max(1, Number(form.xpMultiplier) || 1)
      };
      if (form.id) {
        await updateEvent(form.id, payload);
        enqueueSnackbar('Evento actualizado', { variant: 'success' });
      } else {
        await createEvent(payload);
        enqueueSnackbar('Evento creado', { variant: 'success' });
      }
      handleReset();
      await load();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo guardar el evento', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await deleteEvent(id);
      enqueueSnackbar('Evento eliminado', { variant: 'success' });
      if (form.id === id) handleReset();
      await load();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo eliminar el evento', { variant: 'error' });
    }
  };

  if (loading) return <div className="geo-admin-empty">Cargando eventos...</div>;

  return (
    <div className="geo-admin-wrap">
      <header className="geo-admin-header">
        <div>
          <h2>Eventos temporales</h2>
          <p>Programá fechas especiales (Día de los Museos, Noche de los Museos, vacaciones) con bono de XP para tus visitantes.</p>
        </div>
        <button className="geo-btn geo-btn-secondary" onClick={handleReset}>Nuevo evento</button>
      </header>

      <div className="geo-admin-grid">
        <aside className="geo-card">
          <h3>Eventos programados</h3>
          {events.length === 0 ? (
            <p className="geo-muted">Todavía no creaste ningún evento.</p>
          ) : (
            <div className="geo-scene-list">
              {events.map((ev) => {
                const status = statusOf(ev);
                return (
                  <div key={ev.id} className={`geo-scene-item ${form.id === ev.id ? 'active' : ''}`}>
                    <button className="geo-scene-item-main" onClick={() => handleEdit(ev)}>
                      <strong>{ev.name || 'Sin nombre'}</strong>
                      <span>
                        <span className={`event-status-dot is-${status.tone}`} /> {status.label}
                        {ev.xpMultiplier > 1 ? ` · XP x${ev.xpMultiplier}` : ''}
                      </span>
                    </button>
                    <button className="geo-icon-btn danger" onClick={() => handleDelete(ev.id)} title="Eliminar">
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        <section className="geo-card">
          <h3>{form.id ? 'Editar evento' : 'Nuevo evento'}</h3>
          <div className="geo-form-grid">
            <label>
              Nombre del evento
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Noche de los Museos"
              />
            </label>
            <label>
              Bono de XP (multiplicador)
              <input
                type="number"
                min="1"
                step="0.5"
                value={form.xpMultiplier}
                onChange={(e) => setForm((f) => ({ ...f, xpMultiplier: e.target.value }))}
              />
            </label>
          </div>

          <label className="geo-block">
            Descripción
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Qué encontrarán los visitantes durante este evento."
            />
          </label>

          <div className="geo-form-grid">
            <label>
              Inicio
              <input
                type="datetime-local"
                value={form.startAt}
                onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
              />
            </label>
            <label>
              Fin
              <input
                type="datetime-local"
                value={form.endAt}
                onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
              />
            </label>
          </div>

          <div className="geo-actions">
            <button className="geo-btn geo-btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? 'Guardando...' : (form.id ? 'Actualizar evento' : 'Crear evento')}
            </button>
            {form.id && (
              <button className="geo-btn geo-btn-secondary" onClick={handleReset}>Cancelar edición</button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
